<script lang="ts" setup>
import type { LinkGroup } from '.';

import { Container } from '@/components/Container';
import { TabSwitcher } from '@/components/ui/TabSwitcher';
import { motion } from 'motion-v';

interface Props {
	title: string;
	tabs: readonly string[];
	selectedTab: string;
	groups: LinkGroup[];
	layoutId?: string;
	bgClass?: string;
}

withDefaults(defineProps<Props>(), {
	layoutId: 'tab-underline',
	bgClass: 'bg-white-dark-1'
});

const emit = defineEmits<{
	'update:selectedTab': [value: string];
	'link-click': [item: LinkGroup['items'][number]];
}>();

function handleTabChange(tab: string) {
	emit('update:selectedTab', tab);
}

function handleLinkClick(item: LinkGroup['items'][number]) {
	emit('link-click', item);
}
</script>

<template>
	<section class="w-full" :class="bgClass">
		<Container
			class="max-w-full! flex-1 overflow-hidden"
			main-class="flex flex-col gap-6"
		>
			<header class="flex flex-col items-start">
				<h2
					class="font-matter-sq text-[28px] font-normal leading-normal! tracking-[-0.48px] text-primary md:text-2xl lg:text-[32px] lg:tracking-[-0.64px]"
				>
					{{ title }}
				</h2>
			</header>

			<!-- Tabs -->
			<TabSwitcher
				:tabs="tabs"
				:model-value="selectedTab"
				:layout-id="layoutId"
				@update:model-value="handleTabChange"
			/>
			<!-- eslint-disable-next-line vue/component-name-in-template-casing -->
			<motion.div
				:key="selectedTab"
				class="no-scrollbar -mx-5 flex snap-x snap-mandatory scroll-pl-5 flex-nowrap gap-6 overflow-x-scroll md:-mx-8 md:scroll-pl-8 lg:mx-0 lg:grid lg:snap-none lg:grid-cols-2 lg:gap-6 xl:grid-cols-4"
				layout
				:initial="false"
			>
				<!-- eslint-disable-next-line vue/component-name-in-template-casing -->
				<motion.div
					v-for="group in groups"
					:key="group.title + selectedTab"
					class="flex shrink-0 basis-[88%] snap-start flex-col gap-4 first:pl-5 last:mr-[12%] md:basis-[44%] md:first:pl-8 lg:first:pl-0 lg:last:mr-0"
				>
					<h3
						class="font-matter-sq text-2xl font-normal leading-normal! tracking-[-0.48px] text-primary"
					>
						{{ group.title }}
					</h3>
					<ul class="flex flex-col gap-4">
						<li
							v-for="item in group.items"
							:key="item.href + item.label + selectedTab"
						>
							<NuxtLink :to="item.href" @click="handleLinkClick(item)">
								<TextLink
									as="p"
									class="text-left text-[16px] leading-[1.75] text-primary"
								>
									{{ item.label }}
								</TextLink>
							</NuxtLink>
						</li>
					</ul>
				</motion.div>
			</motion.div>
		</Container>
	</section>
</template>
